import type { EmailDeliverySettings } from '../../settings/model.js';
import type { AuthenticatedUser } from '../auth/model.js';
import { escapeAttribute, escapeHtml } from '../html.js';
import { renderAuthenticatedShell } from '../view.js';
import type {
  NinjaBackupInventoryRow,
  SettingsIntegrationRow,
  SettingsNotificationGroupRow,
  SettingsPageModel,
} from './model.js';
import {
  settingsSectionHref,
  type SettingsSectionId,
} from './routing.js';
import { endpointKey, filteredAssignmentEndpoints } from './group-assignment.js';

export function renderSettings(
  model: SettingsPageModel,
  user: AuthenticatedUser,
  managedClientCount: number,
  activeSection: SettingsSectionId | null = null,
): string {
  return renderAuthenticatedShell(
    user,
    managedClientCount,
    'SETTINGS',
    `
      <main class="workspace settings-workspace" id="settings">
        <header class="page-heading settings-heading">
          <div><h1>Settings</h1><p>NinjaOne inventory and backup alert routing</p></div>
          <span class="mock-mode-label">Local validation</span>
        </header>
        <div class="settings-layout">
          ${renderSettingsIndex(activeSection)}
          <div class="settings-content">
            ${renderBackupInventory(model)}
            ${renderEmailDelivery(model.emailDelivery)}
            ${renderPrimaryRecipients(model.primaryBackupAlertRecipients)}
            ${renderAdditionalGroups(model.additionalNotificationGroups)}
            ${renderDeviceAssignments(model)}
            ${renderAuthentication(model)}
            ${renderIntegrations(model.integrations)}
            <p class="settings-feedback" data-settings-feedback aria-live="polite"></p>
          </div>
        </div>
        <p class="prototype-note">Local validation only · Automatic alert delivery remains disabled</p>
      </main>
    `,
  );
}

export function renderSettingsLoading(
  user: AuthenticatedUser,
  managedClientCount: number,
): string {
  return renderAuthenticatedShell(
    user,
    managedClientCount,
    'SETTINGS',
    `<main class="workspace settings-workspace" id="settings"><header class="page-heading"><h1>Settings</h1><p>Loading persisted configuration…</p></header></main>`,
  );
}

function renderSettingsIndex(activeSection: SettingsSectionId | null): string {
  const selected = activeSection ?? 'ninja-backup-inventory';
  return `
    <nav class="settings-index" aria-label="Settings sections">
      ${settingsIndexLink('ninja-backup-inventory', 'Ninja Backup Inventory', selected)}
      ${settingsIndexLink('email-delivery', 'Email Delivery', selected)}
      ${settingsIndexLink('primary-recipients', 'Primary Alert Recipients', selected)}
      ${settingsIndexLink('additional-groups', 'Additional Groups', selected)}
      ${settingsIndexLink('device-group-assignments', 'Device Group Assignments', selected)}
      ${settingsIndexLink('authentication', 'Authentication', selected)}
      ${settingsIndexLink('integrations', 'Integrations', selected)}
    </nav>
  `;
}

function settingsIndexLink(
  section: SettingsSectionId,
  label: string,
  selected: SettingsSectionId,
): string {
  const active = section === selected;
  return `<a href="${settingsSectionHref(section)}" data-app-route data-settings-section="${section}"${active ? ' class="active" aria-current="location"' : ''}>${label}</a>`;
}

function renderBackupInventory(model: SettingsPageModel): string {
  const syncLabel = formatTimestamp(model.inventorySync.lastSynchronizedAt);
  return settingsSection(
    'ninja-backup-inventory',
    'NinjaOne Backup Inventory',
    'Endpoints are included automatically when NinjaOne reports an active Backup assignment.',
    `
      <div class="section-toolbar inventory-toolbar">
        <span data-inventory-sync-state>${model.inventorySync.state === 'MOCK_REFRESHED' ? 'Mock refresh completed' : `Last synchronized ${escapeHtml(syncLabel)}`}</span>
        <button class="secondary-action" type="button" data-refresh-ninja>Refresh from Ninja</button>
      </div>
      <div class="settings-table-wrap">
        <table class="settings-table inventory-table">
          <thead><tr><th>Organization</th><th>Device</th><th>Ninja Backup assignment</th><th>Observed destinations</th><th>Evidence</th><th>Last synchronized</th></tr></thead>
          <tbody>${model.inventory.map(renderInventoryRow).join('')}</tbody>
        </table>
      </div>
      <p class="settings-help">To add or remove an endpoint from monitoring, change its Backup assignment in NinjaOne and refresh this inventory.</p>
    `,
  );
}

function renderInventoryRow(endpoint: NinjaBackupInventoryRow): string {
  return `
    <tr data-inventory-device-id="${endpoint.deviceId}">
      <td><strong>${escapeHtml(endpoint.organizationName)}</strong><small>ID ${endpoint.organizationId}</small></td>
      <td><strong>${escapeHtml(endpoint.deviceName)}</strong><small>ID ${endpoint.deviceId}</small></td>
      <td>${detectedAssignment(endpoint.assignmentMethod)}</td>
      <td><span class="destination-tags">${endpoint.observedDestinations.map(statusPill).join('')}</span></td>
      <td><span class="evidence-label">${endpoint.destinationEvidenceSource === 'OBSERVED_JOB_HISTORY' ? 'Observed job history' : 'Ninja configuration'}</span></td>
      <td><time datetime="${escapeAttribute(endpoint.lastSynchronizedAt)}">${escapeHtml(formatTimestamp(endpoint.lastSynchronizedAt))}</time></td>
    </tr>
  `;
}

function renderEmailDelivery(settings: EmailDeliverySettings): string {
  return settingsSection(
    'email-delivery',
    'Email Delivery',
    'Microsoft Graph delivery identity. A changed sender mailbox must be verified before alert delivery.',
    `
      <form class="settings-form" data-email-delivery-form>
        <div class="settings-form-grid">
          <label><span>Sender display name</span><input name="senderDisplayName" required maxlength="120" value="${escapeAttribute(settings.senderDisplayName)}"></label>
          <label><span>Sender mailbox</span><input name="senderMailbox" type="email" required value="${escapeAttribute(settings.senderMailbox)}"></label>
          <label><span>Provider</span><input value="Microsoft Graph" readonly></label>
          <label><span>Authentication</span><input value="Certificate" readonly></label>
        </div>
        <dl class="settings-definition-list compact-definition-list">
          <div><dt>Delivery status</dt><dd>${statusPill(settings.deliveryStatus)}</dd></div>
          <div><dt>Last verified</dt><dd>${escapeHtml(formatNullableTimestamp(settings.lastVerifiedAt))}</dd></div>
          <div><dt>Certificate thumbprint</dt><dd><code>${escapeHtml(abbreviateThumbprint(settings.certificateThumbprint))}</code></dd></div>
          <div><dt>Certificate expires</dt><dd>${escapeHtml(formatNullableTimestamp(settings.certificateExpiresAt))}</dd></div>
          <div><dt>Last verification result</dt><dd>${escapeHtml(settings.lastVerificationReason ?? 'No verification result')}</dd></div>
        </dl>
        <div class="mailer-test-row">
          <label><span>Test recipient</span><input name="testRecipient" type="email" autocomplete="off" placeholder="operator@example.com" data-test-email-recipient></label>
          <p>Uses the persisted sender mailbox and sends exactly one controlled test message.</p>
        </div>
        <div class="form-actions"><button class="primary-action" type="submit">Save</button><button class="secondary-action" type="button" data-test-email>Test email</button></div>
      </form>
    `,
  );
}

function renderPrimaryRecipients(recipients: readonly string[]): string {
  const rows = recipients.length === 0 ? [''] : recipients;
  return settingsSection(
    'primary-recipients',
    'Primary Backup Alert Recipients',
    'Every actionable backup alert includes these recipients. At least one is required before automatic alerting can be enabled.',
    `
      <form class="settings-form" data-primary-recipients-form>
        <div class="recipient-editor" data-primary-recipient-list>${rows.map(renderPrimaryRecipientInput).join('')}</div>
        <div class="form-actions"><button class="secondary-action" type="button" data-add-primary-recipient>Add recipient</button><button class="primary-action" type="submit">Save</button></div>
      </form>
    `,
  );
}

function renderPrimaryRecipientInput(recipient: string): string {
  return `<label class="recipient-input-row"><span class="sr-only">Primary recipient</span><input type="email" name="recipient" required value="${escapeAttribute(recipient)}"><button class="table-action danger" type="button" data-remove-primary-recipient>Remove</button></label>`;
}

function renderAdditionalGroups(groups: readonly SettingsNotificationGroupRow[]): string {
  return settingsSection(
    'additional-groups',
    'Additional Notification Groups',
    'Optional recipients added to the primary Backup alert route for assigned devices.',
    `
      <details class="settings-editor"><summary>Add group</summary>${renderGroupForm(null)}</details>
      <div class="section-toolbar"><span>${groups.length} groups</span></div>
      <div class="settings-table-wrap">
        <table class="settings-table groups-table">
          <thead><tr><th>Group</th><th>Recipients</th><th>Email addresses</th><th></th></tr></thead>
          <tbody>${groups.map(renderGroupRow).join('')}</tbody>
        </table>
      </div>
    `,
  );
}

function renderGroupRow(group: SettingsNotificationGroupRow): string {
  return `
    <tr>
      <td><strong>${escapeHtml(group.groupName)}</strong><small>${escapeHtml(group.description || 'No description')}</small></td>
      <td>${group.recipients.length}</td>
      <td><span class="recipient-list">${group.recipients.map((recipient) => `<span>${escapeHtml(recipient)}</span>`).join('')}</span></td>
      <td><details class="row-editor"><summary>Edit</summary>${renderGroupForm(group)}</details><button class="table-action danger" type="button" data-delete-group="${escapeAttribute(group.groupId)}">Delete</button></td>
    </tr>
  `;
}

function renderGroupForm(group: SettingsNotificationGroupRow | null): string {
  return `<form class="settings-form group-editor-form" data-group-form data-group-id="${escapeAttribute(group?.groupId ?? '')}">
    <label><span>Group name</span><input name="name" required maxlength="120" value="${escapeAttribute(group?.groupName ?? '')}"></label>
    <label><span>Description</span><input name="description" maxlength="500" value="${escapeAttribute(group?.description ?? '')}"></label>
    <label><span>Recipients <small>One email address per line</small></span><textarea name="recipients" rows="3">${escapeHtml(group?.recipients.join('\n') ?? '')}</textarea></label>
    <button class="primary-action" type="submit">${group === null ? 'Create group' : 'Save group'}</button>
  </form>`;
}

function renderDeviceAssignments(model: SettingsPageModel): string {
  const endpoints = filteredAssignmentEndpoints(model, '');
  const hasGroups = model.additionalNotificationGroups.length > 0;
  return settingsSection(
    'device-group-assignments',
    'Device-to-Additional-Group Assignments',
    'Assignments add recipients without changing dashboard monitoring or the primary alert route.',
    `
      <form class="settings-form assignment-editor group-assignment-editor" data-group-assignment-form>
        <label><span>Additional group</span><select name="groupId" required data-group-assignment-group${hasGroups ? '' : ' disabled'}>
          <option value="">Select an additional group</option>
          ${model.additionalNotificationGroups.map((group) => `<option value="${escapeAttribute(group.groupId)}">${escapeHtml(group.groupName)}</option>`).join('')}
        </select></label>
        <div class="group-assignment-controls" data-group-assignment-controls aria-disabled="true">
          <label><span>Endpoints</span><input type="search" autocomplete="off" placeholder="Search endpoints…" data-group-assignment-search disabled></label>
          <p class="settings-help group-assignment-instruction" data-group-assignment-instruction>${hasGroups ? 'Select an additional group first.' : 'Create an additional notification group first.'}</p>
          <div class="endpoint-checklist" data-group-assignment-endpoints>
            ${endpoints.map((endpoint) => `
              <label class="endpoint-checklist-row" data-group-assignment-endpoint="${escapeAttribute(endpointKey(endpoint))}" data-organization-name="${escapeAttribute(endpoint.organizationName)}" data-device-name="${escapeAttribute(endpoint.deviceName)}">
                <input type="checkbox" name="endpoint" value="${escapeAttribute(endpointKey(endpoint))}" disabled>
                <span><strong>${escapeHtml(endpoint.deviceName)}</strong><small>${escapeHtml(endpoint.organizationName)}</small></span>
              </label>
            `).join('')}
          </div>
          <p class="settings-help" data-group-assignment-empty hidden>No matching endpoints</p>
          <div class="group-assignment-toolbar">
            <div><button class="secondary-action" type="button" data-select-all-filtered disabled>Select all filtered</button><button class="secondary-action" type="button" data-clear-group-assignment disabled>Clear selection</button></div>
            <strong data-group-assignment-count>0 endpoints selected</strong>
          </div>
        </div>
        <div class="form-actions"><button class="primary-action" type="submit" data-save-group-assignment disabled>Save assignment</button></div>
      </form>
      <div class="settings-table-wrap">
        <table class="settings-table assignment-table">
          <thead><tr><th>Additional group</th><th>Assigned endpoints</th><th>Routing behavior</th></tr></thead>
          <tbody>${model.additionalNotificationGroups.map((group) => renderGroupAssignmentSummary(model, group)).join('')}</tbody>
        </table>
      </div>
    `,
  );
}

function renderGroupAssignmentSummary(
  model: SettingsPageModel,
  group: SettingsNotificationGroupRow,
): string {
  const assignments = model.deviceGroupAssignments
    .filter((assignment) => assignment.groupIds.includes(group.groupId))
    .sort((left, right) =>
      left.organizationName.localeCompare(right.organizationName, undefined, { sensitivity: 'base' })
      || left.deviceName.localeCompare(right.deviceName, undefined, { sensitivity: 'base' }));
  const countLabel = `${assignments.length} assigned endpoint${assignments.length === 1 ? '' : 's'}`;
  return `
    <tr>
      <td><strong>${escapeHtml(group.groupName)}</strong><small>${escapeHtml(group.description || 'No description')}</small></td>
      <td>${assignments.length === 0
        ? `<span>${countLabel}</span>`
        : `<details class="assignment-summary"><summary>${countLabel}</summary><ul>${assignments.map((assignment) => `<li><strong>${escapeHtml(assignment.deviceName)}</strong><span>${escapeHtml(assignment.organizationName)}</span></li>`).join('')}</ul></details>`}</td>
      <td><span class="additive-label">Primary + additional groups</span></td>
    </tr>
  `;
}

function renderAuthentication(model: SettingsPageModel): string {
  return settingsSection(
    'authentication',
    'Authentication',
    'Microsoft Entra human sign-in configuration. Read-only in this local validation.',
    `<dl class="settings-definition-list"><div><dt>Identity provider</dt><dd>${escapeHtml(model.authentication.provider)}</dd></div><div><dt>Tenant access</dt><dd>${escapeHtml(model.authentication.tenantScope)}</dd></div><div><dt>Application roles</dt><dd>${model.authentication.roles.map(statusPill).join('')}</dd></div><div><dt>State</dt><dd>${statusPill(model.authentication.mode)}</dd></div></dl>`,
  );
}

function renderIntegrations(integrations: readonly SettingsIntegrationRow[]): string {
  return settingsSection(
    'integrations',
    'Integrations',
    'Global connector and runtime state only. Client links are managed under each Client. Credentials and tokens are never displayed.',
    `<div class="integration-list">${integrations.map(renderIntegration).join('')}</div>`,
  );
}

function renderIntegration(integration: SettingsIntegrationRow): string {
  return `<div class="integration-row"><span><strong>${escapeHtml(integration.integration)}</strong><small>${escapeHtml(integration.detail)}</small></span>${statusPill(integration.status)}</div>`;
}

function settingsSection(id: string, title: string, description: string, body: string): string {
  return `<section class="settings-section" id="${escapeAttribute(id)}" aria-labelledby="${escapeAttribute(id)}-title"><header><h2 id="${escapeAttribute(id)}-title">${escapeHtml(title)}</h2><p>${escapeHtml(description)}</p></header>${body}</section>`;
}

function detectedAssignment(method: NinjaBackupInventoryRow['assignmentMethod']): string {
  return `<span class="boolean-state enabled"><i aria-hidden="true"></i><span><strong>Detected</strong><small>${escapeHtml(titleCase(method))}</small></span></span>`;
}

function statusPill(value: string): string {
  const className = value.toLowerCase().replaceAll('_', '-').replaceAll(' ', '-');
  return `<span class="status-pill status-${className}">${escapeHtml(value.replaceAll('_', ' '))}</span>`;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unknown';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatNullableTimestamp(value: string | null): string {
  return value === null ? 'Not verified' : formatTimestamp(value);
}

function abbreviateThumbprint(value: string | null): string {
  if (value === null) return 'Not recorded';
  return value.length <= 12 ? value : `${value.slice(0, 6)}…${value.slice(-6)}`;
}

function titleCase(value: string): string {
  return value
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}
